import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";

import {
  Calendar,
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  DisabledTooltip,
  Dropdown,
  FormField,
  Icon,
  Stepper,
} from "@/ds";
import type { CalendarProps } from "@/ds";
import { NovoTipoObraDialog } from "../novo-tipo-obra-dialog";
import { CotacaoDialog } from "../cotacao-dialog";
import { EtapaProjecaoView } from "./etapa-projecao/view-etapa-projecao";
import { IdentificacaoEquipamento } from "./identificacao-equipamento";
import {
  ADICIONAR_ESCOPO_TITLE,
  ALTERNATIVAS_LABEL,
  CENTRO_CUSTO_LABEL,
  DADOS_TITLE,
  DESCRICAO_LABEL,
  DESCRICAO_MAX,
  DESCRICAO_PLACEHOLDER,
  ESCOPO_MACRO_LABEL,
  ESCOPO_TEXTO_MAX,
  ESCOPO_TEXTO_PLACEHOLDER,
  ETAPAS_LABEL,
  FIM_LABEL,
  INFO_TIPOS_DA_OBRA,
  INICIO_LABEL,
  NOVO_ESCOPO_TITLE,
  ORIGEM_LABEL,
  PADRAO_LABEL,
  PERSONALIZADO_LABEL,
  SELECIONE_ESCOPO_PLACEHOLDER,
  SELECIONE_PLACEHOLDER,
  TIPO_OBRA_LABEL,
} from "./adicionar-escopo-macro-dialog.constants";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./adicionar-escopo-macro-dialog.module.scss";
import type {
  AdicionarEscopoMacroDialogViewModel,
  OpcaoEscopo,
} from "./types-adicionar-escopo-macro-dialog";

const ROTULO = { label: modal.rotuloCampo };
const ALTERNATIVAS = [
  { label: PADRAO_LABEL, value: "PADRAO" },
  { label: PERSONALIZADO_LABEL, value: "PERSONALIZADO" },
];
const RODAPE_DIALOGO = { footer: { className: modal.dialogoRodape } };

function itemOpcao(opcao: OpcaoEscopo) {
  return (
    <DisabledTooltip content={opcao.tagTitulo} className={styles.itemComTag}>
      {opcao.label}
      {opcao.tag ? (
        <Tag
          className={styles.tag}
          value={opcao.tag}
          severity="warning"
          rounded
          icon="pi pi-hourglass"
        />
      ) : null}
    </DisabledTooltip>
  );
}

function valorEscolhido(opcao: OpcaoEscopo | null, props: { placeholder?: string }) {
  return opcao ? itemOpcao(opcao) : <span>{props.placeholder}</span>;
}

function CampoData(props: CalendarProps) {
  return (
    <span className={styles.campoData}>
      <span className={styles.iconeData} aria-hidden>
        <Icon name="calendar" size={14} />
      </span>
      <Calendar
        appendTo={document.body}
        {...props}
        showIcon={false}
        inputClassName={styles.entradaData}
      />
    </span>
  );
}

type Formulario = NonNullable<AdicionarEscopoMacroDialogViewModel["formulario"]>;

interface FormularioEscopoProps {
  formulario: Formulario;
  vm: Pick<
    AdicionarEscopoMacroDialogViewModel,
    | "escolherAlternativa"
    | "digitarNome"
    | "escolherEscopoPadrao"
    | "alterarInicio"
    | "alterarFim"
    | "escolherOrigem"
    | "digitarDescricao"
    | "enviando"
  >;
}

function FormularioEscopo({ formulario, vm }: FormularioEscopoProps) {
  const { forma, alternancia, nome, catalogo, equipamento, inicio, fim, origem, descricao } =
    formulario;
  const rotuloAlternativa = alternancia === "PADRAO" ? PADRAO_LABEL : PERSONALIZADO_LABEL;

  return (
    <fieldset
      className={styles.formulario}
      disabled={vm.enviando}
      data-testid="adicionar-escopo-formulario"
    >
      <legend className={styles.formularioTitulo}>
        {NOVO_ESCOPO_TITLE}
        {alternancia ? <Tag className={styles.tag} value={rotuloAlternativa} /> : null}
      </legend>

      {alternancia ? (
        <SelectButton
          className={styles.alternancia}
          value={alternancia}
          options={ALTERNATIVAS}
          allowEmpty={false}
          onChange={(event) => vm.escolherAlternativa(event.value)}
          aria-label={ALTERNATIVAS_LABEL}
          data-testid="adicionar-escopo-alternancia"
        />
      ) : null}

      {forma === "TEXTO" ? (
        <FormField.Root required classNames={ROTULO} error={nome.erro}>
          <FormField.Label>{ESCOPO_MACRO_LABEL}</FormField.Label>
          <FormField.Control>
            <InputText
              value={nome.value}
              maxLength={ESCOPO_TEXTO_MAX}
              placeholder={ESCOPO_TEXTO_PLACEHOLDER}
              onChange={(event) => vm.digitarNome(event.currentTarget.value)}
              data-testid="adicionar-escopo-nome"
            />
          </FormField.Control>
          <FormField.Error data-testid="adicionar-escopo-nome-error" />
        </FormField.Root>
      ) : null}

      {forma === "CATALOGO" ? (
        <>
          <FormField.Root required classNames={ROTULO} error={catalogo.erro}>
            <FormField.Label>{ESCOPO_MACRO_LABEL}</FormField.Label>
            <FormField.Control>
              <Dropdown
                value={catalogo.value}
                options={catalogo.opcoes}
                optionDisabled="disabled"
                itemTemplate={itemOpcao}
                placeholder={SELECIONE_ESCOPO_PLACEHOLDER}
                onChange={(event) => vm.escolherEscopoPadrao(event.value)}
                data-testid="adicionar-escopo-padrao"
              />
            </FormField.Control>
            <FormField.Error data-testid="adicionar-escopo-padrao-error" />
          </FormField.Root>
          {catalogo.vazio || catalogo.falha ? (
            <Message
              className="p-message-block"
              severity={catalogo.falha ? "error" : "info"}
              text={catalogo.falha ?? catalogo.vazio}
              data-testid="adicionar-escopo-padrao-vazio"
            />
          ) : null}
        </>
      ) : null}

      {forma === "EQUIPAMENTO" ? <IdentificacaoEquipamento {...equipamento} /> : null}

      <div className={styles.periodo}>
        <FormField.Root required classNames={ROTULO} error={inicio.erro}>
          <FormField.Label>{INICIO_LABEL}</FormField.Label>
          <FormField.Control>
            <CampoData
              value={inicio.value}
              onChange={(event) => vm.alterarInicio(event.value ?? null)}
              data-testid="adicionar-escopo-inicio"
            />
          </FormField.Control>
          <FormField.Error data-testid="adicionar-escopo-inicio-error" />
        </FormField.Root>
        <FormField.Root required classNames={ROTULO} error={fim.erro}>
          <FormField.Label>{FIM_LABEL}</FormField.Label>
          <FormField.Control>
            <CampoData
              value={fim.value}
              onChange={(event) => vm.alterarFim(event.value ?? null)}
              data-testid="adicionar-escopo-fim"
            />
          </FormField.Control>
          <FormField.Error data-testid="adicionar-escopo-fim-error" />
        </FormField.Root>
        <FormField.Root required classNames={ROTULO} error={origem.erro}>
          <FormField.Label>{ORIGEM_LABEL}</FormField.Label>
          <FormField.Control>
            <Dropdown
              value={origem.value}
              options={origem.opcoes}
              placeholder={SELECIONE_PLACEHOLDER}
              onChange={(event) => vm.escolherOrigem(event.value)}
              data-testid="adicionar-escopo-origem"
            />
          </FormField.Control>
          <FormField.Error data-testid="adicionar-escopo-origem-error" />
        </FormField.Root>
      </div>

      <FormField.Root classNames={ROTULO} error={descricao.erro}>
        <FormField.Label>{DESCRICAO_LABEL}</FormField.Label>
        <FormField.Control>
          <InputText
            value={descricao.value}
            placeholder={DESCRICAO_PLACEHOLDER}
            onChange={(event) => vm.digitarDescricao(event.currentTarget.value)}
            data-testid="adicionar-escopo-descricao"
          />
        </FormField.Control>
        <span className={styles.contador} data-testid="adicionar-escopo-descricao-contador">
          {descricao.value.length}/{DESCRICAO_MAX}
        </span>
        <FormField.Error data-testid="adicionar-escopo-descricao-error" />
      </FormField.Root>
    </fieldset>
  );
}

export function AdicionarEscopoMacroDialogView({
  etapa,
  etapas,
  projecao,
  cotacaoDialog,
  aviso,
  centroCusto,
  tipoObra,
  formulario,
  novoTipo,
  erroEnvio,
  enviando,
  rodape,
  escolherCentroCusto,
  escolherTipoObra,
  escolherAlternativa,
  digitarNome,
  escolherEscopoPadrao,
  alterarInicio,
  alterarFim,
  escolherOrigem,
  digitarDescricao,
  cancelar,
  descarte,
  confirmacaoTroca,
}: AdicionarEscopoMacroDialogViewModel) {
  const { salvarSemProjecao } = rodape;

  return (
    <>
      <Dialog
        visible
        size="xl"
        className={modal.dialogoFicha}
        contentClassName={modal.corpo}
        onHide={cancelar}
        focusOnShow={false}
        header={<DialogHeader title={ADICIONAR_ESCOPO_TITLE} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={modal.rodape}
            stackOnMobile
            cancelLabel={rodape.cancelarLabel}
            cancelTestId={rodape.cancelarTestId}
            confirmLabel={rodape.confirmarLabel}
            confirmButtonProps={{
              disabled: rodape.confirmarDesabilitado,
              loading: rodape.confirmarCarregando,
            }}
            extraLabel={salvarSemProjecao?.label}
            extraButtonProps={{
              disabled: salvarSemProjecao?.desabilitado,
              loading: salvarSemProjecao?.carregando,
            }}
            onCancel={rodape.onCancelar}
            onConfirm={rodape.onConfirmar}
            onExtra={salvarSemProjecao?.onClick}
            locked={enviando}
            testId="adicionar-escopo"
            extraTestId="adicionar-escopo-salvar-sem-projecao"
          />
        }
        data-testid="adicionar-escopo-macro-dialog"
      >
        <div className={styles.corpo}>
          <Stepper.Root aria-label={ETAPAS_LABEL}>
            {etapas.map(({ numero, titulo, situacao, ariaLabel, testId, onSelect }) => (
              <Stepper.Step
                key={numero}
                state={situacao}
                number={numero}
                title={titulo}
                ariaLabel={ariaLabel}
                onSelect={onSelect}
                testId={testId}
              />
            ))}
          </Stepper.Root>

          {aviso ? (
            <Message
              className="p-message-block"
              severity="warn"
              text={aviso}
              data-testid="adicionar-escopo-aviso-fel"
            />
          ) : null}

          {etapa === 1 ? (
            <section className={styles.corpo} data-testid="adicionar-escopo-dados">
              <h3 className={styles.titulo}>{DADOS_TITLE}</h3>
              <Message
                severity="info"
                className="p-message-simple p-message-block"
                icon={<Icon name="info-circle" size={18} />}
                text={INFO_TIPOS_DA_OBRA}
                data-testid="adicionar-escopo-info"
              />
              <div className={styles.campos}>
                <FormField.Root
                  required
                  classNames={ROTULO}
                  error={centroCusto.erro}
                  help={centroCusto.ajuda ?? undefined}
                >
                  <FormField.Label>{CENTRO_CUSTO_LABEL}</FormField.Label>
                  <FormField.Control>
                    <Dropdown
                      value={centroCusto.value}
                      options={centroCusto.opcoes}
                      optionDisabled="disabled"
                      itemTemplate={itemOpcao}
                      valueTemplate={valorEscolhido}
                      placeholder={SELECIONE_PLACEHOLDER}
                      onChange={(event) => escolherCentroCusto(event.value)}
                      data-testid="adicionar-escopo-centro-custo"
                    />
                  </FormField.Control>
                  <FormField.Help data-testid="adicionar-escopo-centro-custo-ajuda" />
                  <FormField.Error data-testid="adicionar-escopo-centro-custo-error" />
                </FormField.Root>

                <FormField.Root
                  required
                  classNames={ROTULO}
                  disabled={tipoObra.desabilitado}
                  error={tipoObra.erro}
                  help={tipoObra.ajuda ?? undefined}
                >
                  <FormField.Label>{TIPO_OBRA_LABEL}</FormField.Label>
                  <FormField.Control>
                    <Dropdown
                      value={tipoObra.value}
                      options={tipoObra.opcoes}
                      optionDisabled="disabled"
                      itemTemplate={itemOpcao}
                      valueTemplate={valorEscolhido}
                      placeholder={SELECIONE_PLACEHOLDER}
                      onChange={(event) => escolherTipoObra(event.value)}
                      data-testid="adicionar-escopo-tipo-obra"
                    />
                  </FormField.Control>
                  <FormField.Help data-testid="adicionar-escopo-tipo-obra-ajuda" />
                  <FormField.Error data-testid="adicionar-escopo-tipo-obra-error" />
                </FormField.Root>
              </div>
              {tipoObra.semOpcao ? (
                <Message
                  className="p-message-block"
                  severity="warn"
                  text={tipoObra.semOpcao}
                  data-testid="adicionar-escopo-sem-tipo"
                />
              ) : null}
              {formulario ? (
                <FormularioEscopo
                  formulario={formulario}
                  vm={{
                    escolherAlternativa,
                    digitarNome,
                    escolherEscopoPadrao,
                    alterarInicio,
                    alterarFim,
                    escolherOrigem,
                    digitarDescricao,
                    enviando,
                  }}
                />
              ) : null}
            </section>
          ) : (
            <EtapaProjecaoView {...projecao} />
          )}
          {erroEnvio ? (
            <Message
              className="p-message-block"
              severity="error"
              text={erroEnvio}
              data-testid="adicionar-escopo-erro-envio"
            />
          ) : null}
        </div>
      </Dialog>

      {cotacaoDialog ? <CotacaoDialog {...cotacaoDialog} /> : null}
      {descarte ? <ConfirmDialog {...descarte} /> : null}
      {confirmacaoTroca ? <ConfirmDialog {...confirmacaoTroca} /> : null}
      {novoTipo ? <NovoTipoObraDialog {...novoTipo} /> : null}
    </>
  );
}
